import { useLocation, useNavigate } from "@solidjs/router";
import { OAuthRequestConfig, useStorytellClient } from "@storytell/client";
import { createSignal } from "solid-js";
import { isServer } from "solid-js/web";
import { useWire } from "~/wire";

// Guests go to signup with a post-auth action so the connect resumes after they
// sign in; authenticated callers are redirected to the backend's consent URL.
export function useGoogleConnect() {
  const wire = useWire();
  const client = useStorytellClient();
  const navigate = useNavigate();
  const location = useLocation();

  const [isConnecting, setIsConnecting] = createSignal(false);
  const [error, setError] = createSignal<string | null>(null);

  const connect = async () => {
    if (isServer) return;

    setError(null);

    if (wire.auth.isGuest()) {
      wire.auth.setPostAuthAction("connect-google-drive");
      navigate("/auth/signup");
      return;
    }

    setIsConnecting(true);

    const res = await client.fetch(
      OAuthRequestConfig.withBearerToken(wire.auth.token()),
      undefined,
      // The consent screen is a full-page trip, so the page the user left has
      // to ride along or the return lands on the bare app URL and the org they
      // were working in is gone by the time the redirect has to name one.
      { provider: "google", returnUrl: location.pathname + location.search },
      { projectIdOverride: wire.currentProjectId() }
    );

    if (res.isErr()) {
      console.error("Failed to create OAuth request", res.error);
      setError("Failed to start Google connection. Please try again.");
      setIsConnecting(false);
      return;
    }

    // authURL is base64-encoded by the backend.
    window.location.href = atob(res.value.authURL);
  };

  return { connect, isConnecting, error };
}
